import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import {
  Tag,
  Folder,
  CaretRight,
  CaretDown,
  Plus,
  SpinnerGap,
  Buildings,
  House,
  FileText,
  Image,
  CheckCircle,
  Warning,
  CheckSquare,
  Square,
  ListChecks,
  Sparkle,
  Question,
  ArrowsClockwise,
  Stack,
  FolderOpen,
  MagnifyingGlass,
  // Icons for DD category selection (matching DD settings exactly)
  Fire,
  Lightning,
  Drop,
  Pipe,
  ShieldCheck,
  Wrench,
  Leaf,
  Certificate,
  Scales,
  Thermometer,
  Elevator,
  Plug,
  Fan,
  Eye,
  Lock,
  Key,
  Ruler,
  Users,
  Handshake,
  ClipboardText,
  Clock,
  Calendar,
  MapPin,
  Door,
  Garage,
  Factory,
  Storefront,
  Warehouse,
  Lightbulb,
  Lamp,
  SolarPanel,
  Shower,
  WifiHigh,
  FirstAid,
  Tree,
  Plant,
  Sun,
  CloudRain,
  Wind,
  Snowflake,
  Recycle,
  Hammer,
  Screwdriver,
  PaintRoller,
  Broom,
  Bed,
  Bathtub,
  Toilet,
  CookingPot,
  Notebook,
  Calculator,
  ChartLine,
  ChartBar,
  TreeStructure,
  User,
  Phone,
  Envelope,
  ChatCircle,
  Bell,
  Megaphone,
  Hourglass,
  Timer,
  Heartbeat,
  Heart,
  Pill,
  Stethoscope,
  Syringe,
  Bandaids,
  Money,
  CurrencyEur,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  TrendUp,
  Percent,
  Car,
  Truck,
  Bicycle,
  Train,
  Airplane,
  Boat,
  Star,
  Flag,
  Trophy,
  Target,
  PuzzlePiece,
  Bookmark,
  Archive,
  Package,
  Gift,
  Barcode,
  QrCode,
  Check,
  X,
  Info,
  Camera,
  MusicNote,
  Headphones,
  Television,
  GameController,
  Atom,
  Flask,
  TestTube,
  Microscope,
  GraduationCap,
  BookOpen,
} from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useDueDiligenceCategories, useCreateDueDiligenceCategory } from 'hooks/useDueDiligence';
import { DueDiligenceScope } from 'shared/types/due-diligence';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export type FileLabelResourceType = 'building' | 'unit' | 'attribute';
export type FileLabelDocumentType = 'document' | 'picture';
export type DocumentBehavior = 'replacing' | 'cumulative';

// Available icons for new DD categories - comprehensive list with Dutch labels for search
const DD_CATEGORY_ICONS: { name: string; label: string; icon: React.ComponentType<IconProps> }[] = [
  // Real Estate & Locations
  { name: 'House', label: 'Huis', icon: House },
  { name: 'Buildings', label: 'Gebouwen', icon: Buildings },
  { name: 'MapPin', label: 'Locatie', icon: MapPin },
  { name: 'Door', label: 'Deur', icon: Door },
  { name: 'Garage', label: 'Garage', icon: Garage },
  { name: 'Factory', label: 'Fabriek', icon: Factory },
  { name: 'Storefront', label: 'Winkel', icon: Storefront },
  { name: 'Warehouse', label: 'Magazijn', icon: Warehouse },
  // Technical & Energy
  { name: 'Lightning', label: 'Elektriciteit', icon: Lightning },
  { name: 'Lightbulb', label: 'Lamp', icon: Lightbulb },
  { name: 'Lamp', label: 'Verlichting', icon: Lamp },
  { name: 'Plug', label: 'Stekker', icon: Plug },
  { name: 'SolarPanel', label: 'Zonnepanelen', icon: SolarPanel },
  { name: 'Thermometer', label: 'Temperatuur', icon: Thermometer },
  { name: 'Drop', label: 'Water', icon: Drop },
  { name: 'Shower', label: 'Douche', icon: Shower },
  { name: 'Pipe', label: 'Leiding', icon: Pipe },
  { name: 'Fan', label: 'Ventilatie', icon: Fan },
  { name: 'WifiHigh', label: 'WiFi', icon: WifiHigh },
  { name: 'Elevator', label: 'Lift', icon: Elevator },
  // Safety & Security
  { name: 'Fire', label: 'Brand', icon: Fire },
  { name: 'ShieldCheck', label: 'Veiligheid', icon: ShieldCheck },
  { name: 'FirstAid', label: 'EHBO', icon: FirstAid },
  { name: 'Warning', label: 'Waarschuwing', icon: Warning },
  { name: 'Lock', label: 'Slot', icon: Lock },
  { name: 'Key', label: 'Sleutel', icon: Key },
  { name: 'Eye', label: 'Bewaking', icon: Eye },
  // Environment
  { name: 'Leaf', label: 'Milieu', icon: Leaf },
  { name: 'Tree', label: 'Boom', icon: Tree },
  { name: 'Plant', label: 'Plant', icon: Plant },
  { name: 'Sun', label: 'Zon', icon: Sun },
  { name: 'CloudRain', label: 'Regen', icon: CloudRain },
  { name: 'Wind', label: 'Wind', icon: Wind },
  { name: 'Snowflake', label: 'Sneeuw', icon: Snowflake },
  { name: 'Recycle', label: 'Recyclage', icon: Recycle },
  // Tools & Maintenance
  { name: 'Wrench', label: 'Moersleutel', icon: Wrench },
  { name: 'Hammer', label: 'Hamer', icon: Hammer },
  { name: 'Screwdriver', label: 'Schroevendraaier', icon: Screwdriver },
  { name: 'PaintRoller', label: 'Verfroller', icon: PaintRoller },
  { name: 'Broom', label: 'Bezem', icon: Broom },
  { name: 'Ruler', label: 'Meetlat', icon: Ruler },
  // Interior
  { name: 'Bed', label: 'Bed', icon: Bed },
  { name: 'Bathtub', label: 'Bad', icon: Bathtub },
  { name: 'Toilet', label: 'Toilet', icon: Toilet },
  { name: 'CookingPot', label: 'Keuken', icon: CookingPot },
  // Documents & Measurements
  { name: 'ClipboardText', label: 'Document', icon: ClipboardText },
  { name: 'FileText', label: 'Bestand', icon: FileText },
  { name: 'Notebook', label: 'Notitieboek', icon: Notebook },
  { name: 'Certificate', label: 'Certificaat', icon: Certificate },
  { name: 'Calculator', label: 'Rekenmachine', icon: Calculator },
  { name: 'ChartLine', label: 'Grafiek lijn', icon: ChartLine },
  { name: 'ChartBar', label: 'Grafiek staaf', icon: ChartBar },
  { name: 'Scales', label: 'Weegschaal', icon: Scales },
  { name: 'TreeStructure', label: 'Structuur', icon: TreeStructure },
  { name: 'Stack', label: 'Stapel', icon: Stack },
  // People & Communication
  { name: 'Users', label: 'Gebruikers', icon: Users },
  { name: 'User', label: 'Gebruiker', icon: User },
  { name: 'Handshake', label: 'Handdruk', icon: Handshake },
  { name: 'Phone', label: 'Telefoon', icon: Phone },
  { name: 'Envelope', label: 'Email', icon: Envelope },
  { name: 'ChatCircle', label: 'Chat', icon: ChatCircle },
  { name: 'Bell', label: 'Bel', icon: Bell },
  { name: 'Megaphone', label: 'Megafoon', icon: Megaphone },
  // Time & Calendar
  { name: 'Clock', label: 'Klok', icon: Clock },
  { name: 'Calendar', label: 'Kalender', icon: Calendar },
  { name: 'Hourglass', label: 'Zandloper', icon: Hourglass },
  { name: 'Timer', label: 'Timer', icon: Timer },
  // Health & Medical
  { name: 'Heartbeat', label: 'Hartslag', icon: Heartbeat },
  { name: 'Heart', label: 'Hart', icon: Heart },
  { name: 'Pill', label: 'Pil', icon: Pill },
  { name: 'Stethoscope', label: 'Stethoscoop', icon: Stethoscope },
  { name: 'Syringe', label: 'Spuit', icon: Syringe },
  { name: 'Bandaids', label: 'Pleisters', icon: Bandaids },
  // Business & Finance
  { name: 'Money', label: 'Geld', icon: Money },
  { name: 'CurrencyEur', label: 'Euro', icon: CurrencyEur },
  { name: 'CreditCard', label: 'Creditcard', icon: CreditCard },
  { name: 'Wallet', label: 'Portemonnee', icon: Wallet },
  { name: 'PiggyBank', label: 'Spaarpot', icon: PiggyBank },
  { name: 'Receipt', label: 'Bon', icon: Receipt },
  { name: 'TrendUp', label: 'Trend omhoog', icon: TrendUp },
  { name: 'Percent', label: 'Procent', icon: Percent },
  // Transport
  { name: 'Car', label: 'Auto', icon: Car },
  { name: 'Truck', label: 'Vrachtwagen', icon: Truck },
  { name: 'Bicycle', label: 'Fiets', icon: Bicycle },
  { name: 'Train', label: 'Trein', icon: Train },
  { name: 'Airplane', label: 'Vliegtuig', icon: Airplane },
  { name: 'Boat', label: 'Boot', icon: Boat },
  // Miscellaneous
  { name: 'Star', label: 'Ster', icon: Star },
  { name: 'Flag', label: 'Vlag', icon: Flag },
  { name: 'Trophy', label: 'Trofee', icon: Trophy },
  { name: 'Target', label: 'Doel', icon: Target },
  { name: 'PuzzlePiece', label: 'Puzzel', icon: PuzzlePiece },
  { name: 'Tag', label: 'Tag', icon: Tag },
  { name: 'Bookmark', label: 'Bladwijzer', icon: Bookmark },
  { name: 'Archive', label: 'Archief', icon: Archive },
  { name: 'Package', label: 'Pakket', icon: Package },
  { name: 'Gift', label: 'Cadeau', icon: Gift },
  { name: 'Barcode', label: 'Barcode', icon: Barcode },
  { name: 'QrCode', label: 'QR Code', icon: QrCode },
  { name: 'Check', label: 'Vinkje', icon: Check },
  { name: 'X', label: 'Kruis', icon: X },
  { name: 'Info', label: 'Info', icon: Info },
  { name: 'Camera', label: 'Camera', icon: Camera },
  { name: 'MusicNote', label: 'Muziek', icon: MusicNote },
  { name: 'Headphones', label: 'Koptelefoon', icon: Headphones },
  { name: 'Television', label: 'Televisie', icon: Television },
  { name: 'GameController', label: 'Controller', icon: GameController },
  { name: 'Atom', label: 'Atoom', icon: Atom },
  { name: 'Flask', label: 'Kolf', icon: Flask },
  { name: 'TestTube', label: 'Reageerbuis', icon: TestTube },
  { name: 'Microscope', label: 'Microscoop', icon: Microscope },
  { name: 'GraduationCap', label: 'Diploma', icon: GraduationCap },
  { name: 'BookOpen', label: 'Boek', icon: BookOpen },
];

interface FolderNode {
  id: number;
  name: string;
  type: 'generic' | 'hardcoded';
  parent?: FolderNode;
  children?: FolderNode[];
  allowedFileLabels?: any[];
}

export interface SuggestedAnswerOption {
  value: string;
  label: string;
  statusColor: string;
  tooltipLabel?: string;
  displayOrder?: number;
  isDefault?: boolean;
  conditionRoot?: any;
  conditions?: any;
}

export interface SuggestedQuestion {
  fieldName: string;
  displayName: string;
  helpText?: string;
  answerType: 'options' | 'free_text' | 'date' | 'number' | 'boolean' | 'document_exists' | 'extraction';
  evaluationMode?: 'manual' | 'file_based';
  aiExtractionHint?: string;
  answerOptions?: SuggestedAnswerOption[];
  extractionPrompt?: string;
  extractionValueType?: 'number' | 'string' | 'date' | 'boolean';
  extractionLabel?: string;
}

interface ProposeLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposedLabelName: string;
  suggestedResourceType?: FileLabelResourceType;
  suggestedDocumentType?: FileLabelDocumentType;
  suggestedFolderPath?: string[];
  /** Pre-suggested DD questions to show (from AI document analysis) */
  preSuggestedQuestions?: SuggestedQuestion[];
  /** Default document behavior: 'replacing' (default) or 'cumulative' */
  suggestedDocumentBehavior?: DocumentBehavior;
  onLabelCreated: (label: any) => void;
}

/**
 * Recursive folder tree component for selecting a folder
 */
const FolderTree: React.FC<{
  folder: FolderNode;
  level: number;
  selectedFolderId: number | null;
  onSelectFolder: (folder: FolderNode) => void;
}> = ({ folder, level, selectedFolderId, onSelectFolder }) => {
  const [isExpanded, setIsExpanded] = useState(level < 2);
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedFolderId === folder.id;

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded cursor-pointer transition-colors ${
          isSelected
            ? 'bg-moss-100 border border-moss-300'
            : 'hover:bg-neutral-50'
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelectFolder(folder)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-0.5 hover:bg-neutral-200 rounded"
          >
            {isExpanded ? (
              <CaretDown size={12} weight="bold" />
            ) : (
              <CaretRight size={12} weight="bold" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <Folder
          size={16}
          weight={isSelected ? 'fill' : 'duotone'}
          className={isSelected ? 'text-moss-600' : 'text-neutral-secondary-mid'}
        />
        <span className={`text-sm ${isSelected ? 'font-medium text-moss-700' : ''}`}>
          {folder.name}
        </span>
        {isSelected && (
          <CheckCircle size={14} weight="fill" className="text-moss-600 ml-auto" />
        )}
      </div>
      {isExpanded && hasChildren && (
        <div>
          {folder.children!.map((child) => (
            <FolderTree
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Modal for proposing and creating a new file label when no existing label matches
 */
const ProposeLabelModal: React.FC<ProposeLabelModalProps> = ({
  isOpen,
  onClose,
  proposedLabelName,
  suggestedResourceType = 'building',
  suggestedDocumentType = 'document',
  suggestedFolderPath,
  preSuggestedQuestions,
  suggestedDocumentBehavior = 'replacing',
  onLabelCreated,
}) => {
  const { session } = useSession();

  // Form state
  const [labelName, setLabelName] = useState(proposedLabelName);
  const [resourceType, setResourceType] = useState<FileLabelResourceType>(suggestedResourceType);
  const [documentType, setDocumentType] = useState<FileLabelDocumentType>(suggestedDocumentType);
  const [documentBehavior, setDocumentBehavior] = useState<DocumentBehavior>(suggestedDocumentBehavior);
  const [selectedFolder, setSelectedFolder] = useState<FolderNode | null>(null);

  // Data state
  const [folderStructure, setFolderStructure] = useState<FolderNode | null>(null);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // DD Question suggestions state
  const [suggestedQuestions, setSuggestedQuestions] = useState<SuggestedQuestion[]>([]);
  const [selectedQuestionIndices, setSelectedQuestionIndices] = useState<Set<number>>(new Set());
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  // DD Category selection state
  const [categoryMode, setCategoryMode] = useState<'existing' | 'new'>('existing');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('ClipboardText');
  const [categorySearch, setCategorySearch] = useState('');
  const [iconSearch, setIconSearch] = useState('');

  // Fetch existing DD categories based on resource type (scope)
  const ddScope = resourceType === 'unit' ? DueDiligenceScope.UNIT : DueDiligenceScope.BUILDING;
  const { categories: ddCategories, isLoading: isLoadingCategories } = useDueDiligenceCategories(ddScope);
  const { createCategory: createDDCategory, isCreating: _isCreatingCategory } = useCreateDueDiligenceCategory();

  // Filter categories by search
  const filteredCategories = ddCategories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setLabelName(proposedLabelName);
      setResourceType(suggestedResourceType);
      setDocumentType(suggestedDocumentType);
      setDocumentBehavior(suggestedDocumentBehavior);
      setSelectedFolder(null);

      // Reset category selection
      setCategoryMode('existing');
      setSelectedCategoryId(null);
      setNewCategoryName('');
      setNewCategoryIcon('ClipboardText');
      setCategorySearch('');
      setIconSearch('');

      // If pre-suggested questions are provided, use them instead of fetching
      if (preSuggestedQuestions && preSuggestedQuestions.length > 0) {
        setSuggestedQuestions(preSuggestedQuestions);
        setSelectedQuestionIndices(new Set(preSuggestedQuestions.map((_, i) => i)));
      } else {
        setSuggestedQuestions([]);
        setSelectedQuestionIndices(new Set());
      }
    }
  }, [isOpen, proposedLabelName, suggestedResourceType, suggestedDocumentType, suggestedDocumentBehavior, preSuggestedQuestions]);

  // Fetch DD question suggestions based on label name
  const fetchQuestionSuggestions = useCallback(
    debounce(async (labelNameValue: string, docType: FileLabelDocumentType) => {
      if (!session || !labelNameValue.trim() || labelNameValue.length < 3) {
        setSuggestedQuestions([]);
        setSelectedQuestionIndices(new Set());
        return;
      }

      setIsLoadingQuestions(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/due-diligence-categories/suggest-questions`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              labelName: labelNameValue.trim(),
              documentType: docType,
            }),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Failed to fetch question suggestions');
        }

        const data = await response.json();
        const suggestions = data.suggestions || [];
        setSuggestedQuestions(suggestions);
        // Pre-select all questions by default
        setSelectedQuestionIndices(new Set(suggestions.map((_: SuggestedQuestion, i: number) => i)));
      } catch (error) {
        console.error('Error fetching question suggestions:', error);
        setSuggestedQuestions([]);
      } finally {
        setIsLoadingQuestions(false);
      }
    }, 500),
    [session]
  );

  // Fetch suggestions when label name or document type changes (only if no pre-suggested questions)
  useEffect(() => {
    if (isOpen && labelName.trim().length >= 3 && !preSuggestedQuestions?.length) {
      fetchQuestionSuggestions(labelName, documentType);
    }
  }, [isOpen, labelName, documentType, fetchQuestionSuggestions, preSuggestedQuestions]);

  // Toggle question selection
  const toggleQuestionSelection = (index: number) => {
    setSelectedQuestionIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Select all questions
  const selectAllQuestions = () => {
    setSelectedQuestionIndices(new Set(suggestedQuestions.map((_, i) => i)));
  };

  // Deselect all questions
  const deselectAllQuestions = () => {
    setSelectedQuestionIndices(new Set());
  };

  // Fetch folder structure
  const fetchFolderStructure = useCallback(async () => {
    if (!session) return;

    setIsLoadingFolders(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/architecture?rootFolder=building`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to fetch folder structure');
      }

      const data = await response.json();
      setFolderStructure(data);

      // Try to auto-select suggested folder if provided
      if (suggestedFolderPath && suggestedFolderPath.length > 0) {
        // TODO: Navigate to suggested folder path
      }
    } catch (error) {
      console.error('Error fetching folder structure:', error);
      toast.error('Kon mappenstructuur niet laden');
    } finally {
      setIsLoadingFolders(false);
    }
  }, [session, suggestedFolderPath]);

  useEffect(() => {
    if (isOpen) {
      fetchFolderStructure();
    }
  }, [isOpen, fetchFolderStructure]);

  // Create label and connect to folder
  const handleCreateLabel = async () => {
    if (!labelName.trim()) {
      toast.error('Vul een labelnaam in');
      return;
    }

    if (!selectedFolder) {
      toast.error('Selecteer een map om het label aan te koppelen');
      return;
    }

    // Validate category selection if questions are being created
    const selectedQuestions = suggestedQuestions.filter((_, i) => selectedQuestionIndices.has(i));
    if (selectedQuestions.length > 0) {
      if (categoryMode === 'existing' && !selectedCategoryId) {
        toast.error('Selecteer een categorie voor de DD vragen');
        return;
      }
      if (categoryMode === 'new' && !newCategoryName.trim()) {
        toast.error('Vul een naam in voor de nieuwe categorie');
        return;
      }
    }

    setIsCreating(true);
    try {
      // Step 1: Create the file label with AI settings auto-enabled
      const createResponse = await fetchWithAuth(
        `${API_URL}/file/file-label`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: labelName.trim(),
            resourceType,
            documentType,
            documentBehavior,
            // For cumulative labels, enable timeline and synthesis by default
            cumulativeConfig: documentBehavior === 'cumulative' ? {
              enableTimeline: true,
              enableSynthesis: true,
            } : null,
            // Auto-enable AI analysis for labels created from dropzone suggestions
            aiAnalysisEnabled: true,
            standardFieldsConfig: {
              documentDate: { enabled: true },
              summary: { enabled: true },
            },
          }),
        },
        session
      );

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Kon label niet aanmaken');
      }

      const createdLabel = await createResponse.json();

      // Step 2: Connect label to the selected folder
      const folderName = selectedFolder.name;
      const parentId = selectedFolder.parent?.id || 0;

      const connectResponse = await fetchWithAuth(
        `${API_URL}/file/folder/${encodeURIComponent(folderName)}/label/${createdLabel.id}/${parentId}`,
        { method: 'POST' },
        session
      );

      if (!connectResponse.ok) {
        // Label was created but folder connection failed
        toast.warning('Label aangemaakt maar koppeling aan map mislukt. Koppel handmatig in instellingen.');
      }

      // Step 3: Create selected DD questions if any
      const questionsToCreate = suggestedQuestions.filter((_, i) => selectedQuestionIndices.has(i));
      let questionsCreated = 0;

      if (questionsToCreate.length > 0) {
        // Determine the category ID - create new category if needed
        let targetCategoryId: number;

        if (categoryMode === 'new') {
          // Create new category
          try {
            const newCategory = await createDDCategory({
              name: newCategoryName.trim(),
              icon: newCategoryIcon,
              scope: ddScope,
            });
            targetCategoryId = newCategory.id;
          } catch (categoryError) {
            console.error('Error creating DD category:', categoryError);
            toast.error('Kon DD categorie niet aanmaken');
            throw categoryError;
          }
        } else {
          targetCategoryId = selectedCategoryId!;
        }

        // Create questions in the selected/created category
        // IMPORTANT: Match the exact same format as DueDiligenceCategoryEditorPage.tsx
        let questionDisplayOrder = 0;
        for (const question of questionsToCreate) {
          try {
            // Auto-generate fieldName from displayName if not provided (same as settings page)
            const questionFieldName = question.fieldName || question.displayName
              .toLowerCase()
              .replace(/\s+/g, '_')
              .replace(/[^a-z0-9_]/g, '');

            const questionPayload = {
              fieldName: questionFieldName,
              displayName: question.displayName,
              helpText: question.helpText || '',
              displayOrder: questionDisplayOrder++,
              answerType: question.answerType,
              documentLabelIds: [createdLabel.id],
              aiExtractionHint: question.aiExtractionHint || undefined,
              evaluationMode: question.evaluationMode || 'file_based',
              // Include statusEvaluationConfig for proper file-based evaluation
              statusEvaluationConfig: undefined,
              // Extraction fields
              extractionValueType: question.extractionValueType || undefined,
              extractionPrompt: question.extractionPrompt || undefined,
              extractionLabel: question.extractionLabel || undefined,
            };

            console.log('[ProposeLabelModal] Creating question:', questionPayload);

            // Create the question in the category
            const questionResponse = await fetchWithAuth(
              `${API_URL}/due-diligence-categories/${targetCategoryId}/questions`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(questionPayload),
              },
              session
            );

            if (questionResponse.ok) {
              const createdQuestion = await questionResponse.json();

              // Create the answer options for this question if any
              // IMPORTANT: Match the exact same format as DueDiligenceCategoryEditorPage.tsx
              if (question.answerOptions && question.answerOptions.length > 0) {
                for (const option of question.answerOptions) {
                  try {
                    // Auto-generate value from label if not provided (same as settings page)
                    const optionValue = option.value || option.label
                      .toLowerCase()
                      .replace(/\s+/g, '_')
                      .replace(/[^a-z0-9_]/g, '');

                    const optionPayload = {
                      value: optionValue,
                      label: option.label,
                      statusColor: option.statusColor,
                      tooltipLabel: option.tooltipLabel || option.label,
                      isDefault: option.isDefault || false,
                      displayOrder: option.displayOrder || 0,
                      // Use conditionRoot only (not deprecated conditions field)
                      conditionRoot: option.conditionRoot || null,
                    };

                    console.log('[ProposeLabelModal] Creating answer option:', optionPayload);

                    await fetchWithAuth(
                      `${API_URL}/due-diligence-categories/${targetCategoryId}/questions/${createdQuestion.id}/options`,
                      {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(optionPayload),
                      },
                      session
                    );
                  } catch (optionError) {
                    console.error('Error creating answer option:', optionError);
                  }
                }
              }

              questionsCreated++;
            }
          } catch (error) {
            console.error('Error creating DD question:', error);
          }
        }

        const categoryName = categoryMode === 'new' ? newCategoryName.trim() :
          ddCategories.find(c => c.id === selectedCategoryId)?.name || 'categorie';

        if (questionsCreated === questionsToCreate.length) {
          toast.success(
            `Label "${labelName}" aangemaakt met ${questionsCreated} ${questionsCreated === 1 ? 'vraag' : 'vragen'} in "${categoryName}"`
          );
        } else if (questionsCreated > 0) {
          toast.warning(
            `Label aangemaakt, maar slechts ${questionsCreated} van ${questionsToCreate.length} vragen konden worden aangemaakt`
          );
        } else {
          toast.warning('Label aangemaakt maar DD vragen konden niet worden aangemaakt');
        }
      } else {
        toast.success(`Label "${labelName}" aangemaakt en gekoppeld aan map "${folderName}"`);
      }

      // Return the created label
      onLabelCreated(createdLabel);
      onClose();
    } catch (error) {
      console.error('Error creating label:', error);
      toast.error(error instanceof Error ? error.message : 'Kon label niet aanmaken');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Plus weight="bold" size={20} className="text-moss-600" />
            Nieuw bestandslabel aanmaken
          </DialogTitle>
          <DialogDescription>
            Dit label bestaat nog niet. Maak het aan en koppel het aan een SharePoint map.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
          {/* Label Name */}
          <div className="space-y-2">
            <Label htmlFor="labelName" className="flex items-center gap-1.5">
              <Tag size={14} />
              Labelnaam
            </Label>
            <Input
              id="labelName"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              placeholder="Bijv. Huurcontract, EPC, Vergunning..."
              className="text-sm"
            />
          </div>

          {/* Resource Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Buildings size={14} />
              Resource type
            </Label>
            <Select
              value={resourceType}
              onValueChange={(value: FileLabelResourceType) => setResourceType(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="building">
                  <div className="flex items-center gap-2">
                    <Buildings size={14} />
                    <span>Gebouw</span>
                  </div>
                </SelectItem>
                <SelectItem value="unit">
                  <div className="flex items-center gap-2">
                    <House size={14} />
                    <span>Unit</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <FileText size={14} />
              Document type
            </Label>
            <Select
              value={documentType}
              onValueChange={(value: FileLabelDocumentType) => setDocumentType(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="document">
                  <div className="flex items-center gap-2">
                    <FileText size={14} />
                    <span>Document</span>
                  </div>
                </SelectItem>
                <SelectItem value="picture">
                  <div className="flex items-center gap-2">
                    <Image size={14} />
                    <span>Foto</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Document Behavior (cumulative vs replacing) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Stack size={14} />
              Document gedrag
            </Label>
            <Select
              value={documentBehavior}
              onValueChange={(value: DocumentBehavior) => setDocumentBehavior(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="replacing">
                  <div className="flex items-center gap-2">
                    <ArrowsClockwise size={14} />
                    <div className="flex flex-col">
                      <span>Vervangend</span>
                      <span className="text-[10px] text-neutral-secondary-mid">
                        Nieuw document vervangt oud (bijv. EPC, keuringsattest)
                      </span>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="cumulative">
                  <div className="flex items-center gap-2">
                    <Stack size={14} />
                    <div className="flex flex-col">
                      <span>Cumulatief</span>
                      <span className="text-[10px] text-neutral-secondary-mid">
                        Documenten bouwen op elkaar (bijv. vergunningen, beslissingen)
                      </span>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {documentBehavior === 'cumulative' && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-md">
                <strong>Cumulatief:</strong> Alle documenten met dit label blijven beschikbaar in een tijdlijn.
                AI kan de documenten synthetiseren tot een samenvattend overzicht.
              </p>
            )}
          </div>

          {/* Folder Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Folder size={14} />
              SharePoint map
              <span className="text-red-500">*</span>
            </Label>
            <div className="border rounded-lg max-h-60 overflow-y-auto bg-white">
              {isLoadingFolders ? (
                <div className="flex items-center justify-center py-8">
                  <SpinnerGap size={20} className="animate-spin text-neutral-secondary-mid" />
                  <span className="ml-2 text-sm text-neutral-secondary-mid">
                    Mappenstructuur laden...
                  </span>
                </div>
              ) : folderStructure ? (
                <FolderTree
                  folder={folderStructure}
                  level={0}
                  selectedFolderId={selectedFolder?.id || null}
                  onSelectFolder={setSelectedFolder}
                />
              ) : (
                <div className="flex items-center justify-center py-8 text-sm text-neutral-secondary-mid">
                  <Warning size={16} className="mr-2" />
                  Kon mappenstructuur niet laden
                </div>
              )}
            </div>
            {selectedFolder && (
              <p className="text-xs text-moss-600 flex items-center gap-1">
                <CheckCircle size={12} weight="fill" />
                Geselecteerd: {selectedFolder.name}
              </p>
            )}
          </div>

          {/* DD Category Selection - Only show when questions will be created */}
          {labelName.trim().length >= 3 && suggestedQuestions.length > 0 && selectedQuestionIndices.size > 0 && (
            <div className="space-y-3">
              <Label className="flex items-center gap-1.5">
                <FolderOpen size={14} />
                DD Categorie voor vragen
                <span className="text-red-500">*</span>
              </Label>

              {/* Mode selection: Existing or New category */}
              <RadioGroup
                value={categoryMode}
                onValueChange={(value: 'existing' | 'new') => setCategoryMode(value)}
                className="flex gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="existing" id="category-existing" />
                  <label htmlFor="category-existing" className="text-sm cursor-pointer">
                    Bestaande categorie
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="new" id="category-new" />
                  <label htmlFor="category-new" className="text-sm cursor-pointer">
                    Nieuwe categorie aanmaken
                  </label>
                </div>
              </RadioGroup>

              {categoryMode === 'existing' ? (
                <div className="space-y-2">
                  {/* Search existing categories */}
                  <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      placeholder="Zoek categorie..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Category list */}
                  <div className="border rounded-lg bg-white max-h-48 overflow-y-auto">
                    {isLoadingCategories ? (
                      <div className="flex items-center justify-center py-6">
                        <SpinnerGap size={18} className="animate-spin text-neutral-400" />
                        <span className="ml-2 text-sm text-neutral-500">Categorieën laden...</span>
                      </div>
                    ) : filteredCategories.length > 0 ? (
                      <div className="divide-y divide-neutral-100">
                        {filteredCategories.map((category) => {
                          const isSelected = selectedCategoryId === category.id;
                          const IconComponent = DD_CATEGORY_ICONS.find(i => i.name === category.icon)?.icon || ClipboardText;
                          return (
                            <div
                              key={category.id}
                              className={`p-3 cursor-pointer transition-colors flex items-center gap-3 ${
                                isSelected ? 'bg-moss-50' : 'hover:bg-neutral-50'
                              }`}
                              onClick={() => setSelectedCategoryId(category.id)}
                            >
                              <div className={`p-1.5 rounded ${isSelected ? 'bg-moss-100' : 'bg-neutral-100'}`}>
                                <IconComponent size={16} weight={isSelected ? 'fill' : 'duotone'} className={isSelected ? 'text-moss-600' : 'text-neutral-500'} />
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${isSelected ? 'text-moss-700' : ''}`}>
                                  {category.name}
                                </p>
                                <p className="text-xs text-neutral-400">
                                  {category.questions?.length || 0} vragen
                                </p>
                              </div>
                              {isSelected && (
                                <CheckCircle size={18} weight="fill" className="text-moss-600" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="py-6 text-center text-sm text-neutral-500">
                        {categorySearch ? 'Geen categorieën gevonden' : 'Geen categorieën beschikbaar'}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* New category name */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-neutral-600">Categorie naam</Label>
                    <Input
                      placeholder="Bijv. Waterhuishouding"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                  </div>

                  {/* Icon selection with search */}
                  <div className="space-y-1.5">
                    <Label className="text-xs text-neutral-600">Kies icoon</Label>
                    <div className="border rounded-lg p-3 bg-white space-y-3">
                      {/* Icon search input */}
                      <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input
                          placeholder="Zoek icoon (bijv. brand, water, veiligheid...)"
                          value={iconSearch}
                          onChange={(e) => setIconSearch(e.target.value)}
                          className="pl-9 text-sm"
                        />
                      </div>

                      {/* Filtered icons grid */}
                      <div className="max-h-48 overflow-y-auto">
                        {(() => {
                          const filteredIcons = iconSearch.trim()
                            ? DD_CATEGORY_ICONS.filter(({ name, label }) =>
                                name.toLowerCase().includes(iconSearch.toLowerCase()) ||
                                label.toLowerCase().includes(iconSearch.toLowerCase())
                              )
                            : DD_CATEGORY_ICONS;

                          if (filteredIcons.length === 0) {
                            return (
                              <div className="py-4 text-center text-sm text-neutral-400">
                                Geen iconen gevonden voor "{iconSearch}"
                              </div>
                            );
                          }

                          return (
                            <div className="grid grid-cols-8 gap-2">
                              {filteredIcons.map(({ name, label, icon: IconComp }) => {
                                const isSelected = newCategoryIcon === name;
                                return (
                                  <button
                                    key={name}
                                    type="button"
                                    onClick={() => setNewCategoryIcon(name)}
                                    className={`p-2 rounded-lg transition-colors flex flex-col items-center gap-1 ${
                                      isSelected
                                        ? 'bg-moss-100 ring-2 ring-moss-500'
                                        : 'hover:bg-neutral-100'
                                    }`}
                                    title={label}
                                  >
                                    <IconComp
                                      size={20}
                                      weight={isSelected ? 'fill' : 'duotone'}
                                      className={isSelected ? 'text-moss-600' : 'text-neutral-500'}
                                    />
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Show selected icon name */}
                      {newCategoryIcon && (
                        <div className="text-xs text-neutral-500 flex items-center gap-1.5 pt-2 border-t border-neutral-100">
                          {(() => {
                            const selectedIcon = DD_CATEGORY_ICONS.find(i => i.name === newCategoryIcon);
                            if (selectedIcon) {
                              const IconComp = selectedIcon.icon;
                              return (
                                <>
                                  <IconComp size={14} weight="fill" className="text-moss-600" />
                                  <span>Geselecteerd: <strong>{selectedIcon.label}</strong></span>
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Validation message */}
              {categoryMode === 'existing' && !selectedCategoryId && (
                <p className="text-xs text-amber-600">Selecteer een categorie om de vragen aan toe te voegen</p>
              )}
              {categoryMode === 'new' && !newCategoryName.trim() && (
                <p className="text-xs text-amber-600">Voer een naam in voor de nieuwe categorie</p>
              )}
            </div>
          )}

          {/* DD Question Suggestions */}
          {labelName.trim().length >= 3 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <Question size={14} />
                  Due Diligence vragen
                  <span className="text-neutral-secondary-mid font-normal">(optioneel)</span>
                </Label>
                {suggestedQuestions.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllQuestions}
                      className="text-xs text-moss-600 hover:text-moss-700 flex items-center gap-1"
                    >
                      <ListChecks size={12} />
                      Alles selecteren
                    </button>
                    <span className="text-neutral-300">|</span>
                    <button
                      type="button"
                      onClick={deselectAllQuestions}
                      className="text-xs text-neutral-secondary-mid hover:text-neutral-secondary"
                    >
                      Alles deselecteren
                    </button>
                  </div>
                )}
              </div>

              <div className="border rounded-lg bg-white">
                {isLoadingQuestions ? (
                  <div className="flex items-center justify-center py-6">
                    <SpinnerGap size={18} className="animate-spin text-violet-500" />
                    <span className="ml-2 text-sm text-neutral-secondary-mid">
                      Suggesties laden...
                    </span>
                  </div>
                ) : suggestedQuestions.length > 0 ? (
                  <div className="divide-y divide-neutral-100">
                    {suggestedQuestions.map((question, index) => {
                      const isSelected = selectedQuestionIndices.has(index);
                      return (
                        <div
                          key={index}
                          className={`p-3 cursor-pointer transition-colors ${
                            isSelected ? 'bg-violet-50' : 'hover:bg-neutral-50'
                          }`}
                          onClick={() => toggleQuestionSelection(index)}
                        >
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">
                              {isSelected ? (
                                <CheckSquare
                                  size={18}
                                  weight="fill"
                                  className="text-violet-600"
                                />
                              ) : (
                                <Square size={18} className="text-neutral-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${isSelected ? 'text-violet-900' : ''}`}>
                                {question.displayName}
                              </p>
                              {question.helpText && (
                                <p className="text-xs text-neutral-400 mt-0.5">
                                  {question.helpText}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`text-xs px-1.5 py-0.5 rounded ${
                                    question.answerType === 'extraction'
                                      ? 'bg-amber-100 text-amber-700'
                                      : question.answerType === 'document_exists'
                                      ? 'bg-blue-100 text-blue-700'
                                      : question.evaluationMode === 'file_based'
                                      ? 'bg-moss-100 text-moss-700'
                                      : 'bg-neutral-100 text-neutral-600'
                                  }`}
                                >
                                  {question.answerType === 'extraction'
                                    ? 'Extractie'
                                    : question.answerType === 'document_exists'
                                    ? 'Bestandscontrole'
                                    : question.evaluationMode === 'file_based'
                                    ? 'Automatisch'
                                    : question.answerType === 'options'
                                    ? 'Handmatig'
                                    : question.answerType === 'date'
                                    ? 'Datum'
                                    : question.answerType}
                                </span>
                                {question.aiExtractionHint && (
                                  <span className="text-xs text-violet-600 flex items-center gap-0.5">
                                    <Sparkle size={10} weight="fill" />
                                    AI
                                  </span>
                                )}
                                {question.answerOptions && question.answerOptions.length > 0 && (
                                  <span className="text-xs text-neutral-400">
                                    {question.answerOptions.length} opties
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-6 text-sm text-neutral-secondary-mid">
                    <Sparkle size={16} className="mr-2 text-violet-400" />
                    Voer een labelnaam in om DD vragen te suggereren
                  </div>
                )}
              </div>

              {suggestedQuestions.length > 0 && (
                <p className="text-xs text-neutral-secondary-mid">
                  {selectedQuestionIndices.size} van {suggestedQuestions.length} vragen geselecteerd.
                  Je kunt de vragen later aanpassen in instellingen.
                </p>
              )}
            </div>
          )}

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
            <p className="font-medium mb-1">Wat gebeurt er?</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Het label wordt aangemaakt in de database</li>
              <li>Het label wordt gekoppeld aan de geselecteerde SharePoint map</li>
              <li>Bestanden met dit label komen automatisch in deze map terecht</li>
              {selectedQuestionIndices.size > 0 && (
                <li className="text-violet-700">
                  {selectedQuestionIndices.size} due diligence {selectedQuestionIndices.size === 1 ? 'vraag wordt' : 'vragen worden'} aangemaakt
                </li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button variant="outline" onClick={onClose} disabled={isCreating}>
            Annuleren
          </Button>
          <Button
            onClick={handleCreateLabel}
            disabled={isCreating || !labelName.trim() || !selectedFolder}
            className="bg-moss-600 hover:bg-moss-700 text-white disabled:bg-moss-300 disabled:text-white"
          >
            {isCreating ? (
              <>
                <SpinnerGap size={16} className="animate-spin mr-2" />
                Aanmaken...
              </>
            ) : (
              <>
                <Plus size={16} className="mr-2" />
                Label aanmaken
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProposeLabelModal;
